import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import { PendingScore } from "./PendingScore";
import { NoDataScore } from "./NoDataScore";
import { NotApplicableScore } from "./NotApplicableScore";
import { seasonTagText, type AssetHazardResult } from "@/lib/results";
import { isMainRainySeason } from "@shared/precipitation-seasons";

/**
 * Shared four-way status rendering (computed / not_applicable / no_data /
 * pending), used by every score-shaped cell in both By Asset and By Hazard
 * views so the branch lives in exactly one place instead of being
 * re-implemented per cell type. `renderComputed` is only invoked once
 * `status === "computed"` is confirmed, but `AssetHazardResult` isn't a
 * discriminated union, so the individual score fields
 * (exposure/vulnerability/risk) aren't narrowed by that check — callers may
 * still need their own per-field undefined checks.
 */
export function StatusCell({
  cell,
  renderComputed,
}: {
  cell: AssetHazardResult | undefined;
  renderComputed: (cell: AssetHazardResult) => ReactNode;
}) {
  if (!cell || cell.status === "pending") return <PendingScore />;
  if (cell.status === "no_data") return <NoDataScore reason={cell.statusNote} />;
  if (cell.status === "not_applicable") return <NotApplicableScore />;
  return <>{renderComputed(cell)}</>;
}

/**
 * One hazard line's cell(s): a plain StatusCell for most hazards; for
 * Changing Precipitation, its MAM and OND rows side by side, each with its own
 * score — the two seasons are shown together but never combined. A site with
 * a single main-rainy-season row (Malawi) shows that one score; its season
 * tag is left out when the line's label already names it (`seasonInLabel`,
 * see assetHazardLabel).
 */
export function HazardStatusCells({
  cells,
  renderComputed,
  seasonInLabel = false,
}: {
  cells: AssetHazardResult[];
  renderComputed: (cell: AssetHazardResult) => ReactNode;
  seasonInLabel?: boolean;
}) {
  if (!cells.some((c) => c.season) || (seasonInLabel && isSingleMainSeason(cells))) {
    return <StatusCell cell={cells[0]} renderComputed={renderComputed} />;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {cells.map((c) => (
        <span key={c.season} className="inline-flex items-center gap-1">
          <SeasonTag season={c.season} />
          <StatusCell cell={c} renderComputed={renderComputed} />
        </span>
      ))}
    </span>
  );
}

/** True when an asset's precipitation line is one main-rainy-season row. */
export function isSingleMainSeason(cells: readonly AssetHazardResult[]): boolean {
  return cells.length === 1 && isMainRainySeason(cells[0].season);
}

/** A season code (MAM, OND, DJF...) or a summary line ("main"); see seasonTagText. */
export function SeasonTag({ season }: { season: string | undefined }) {
  if (!season) return null;
  return (
    <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
      {seasonTagText(season)}
    </span>
  );
}

export function RiskBadge({ cell }: { cell: AssetHazardResult }) {
  if (cell.risk === undefined) return <PendingScore />; // computed status but risk missing shouldn't happen; degrade safely
  return <Badge className={LEVEL_BADGE_CLASSES[cell.risk]}>{LEVEL_LABELS[cell.risk]}</Badge>;
}
